import * as React from 'react'
import { connect } from 'react-redux'
import * as ReactStrap from 'reactstrap'
import * as Common from '../common'
import * as Utils from '../common/utils'
import * as Actions from './actions'
import { WrappedSurveyResults } from './reducer'
import * as queryString from 'query-string'

interface DispatchProps {
  fetchSurveyResults: (surveyId: string, communityIdentifier: string) => void;
  redirectToFinalUrl: (finalUrl: string) => void;
  loadSurveyResultsPage: (communityIdentifier: string, surveyId: string, token?: string, surveyOptionId?: string) => void;
}

type Props = DispatchProps & WrappedSurveyResults;

const getResultCardStyle = (configurations: any, percentage: number) => {
  const cardSize = Utils.keyValue(configurations, 'survey_results_card_size', 'large');
  const borderColor = Utils.keyValue(configurations, 'survey_results_border_color', '#dee2e6');
  const highlightColor = Utils.keyValue(configurations, 'survey_results_highlight_color', '#7FAE1B');
  
  const isHighPercentage = percentage > 50;
  
  return {
    transition: 'all 0.3s ease',
    borderRadius: '8px',
    border: `1px solid ${isHighPercentage ? highlightColor : borderColor}`,
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
    height: cardSize === 'small' ? 'auto' : '100%',
    minHeight: cardSize === 'small' ? '80px' : '120px',
    backgroundColor: isHighPercentage ? '#f8f9fa' : '#ffffff',
    marginBottom: '1rem',
  };
};

const ResultCard = ({ 
  option, 
  configurations,
  userSelectedOptionId
}: { 
  option: Actions.SurveyOption; 
  configurations: any;
  userSelectedOptionId?: string;
}) => {
  const cardSize = Utils.keyValue(configurations, 'survey_results_card_size', 'large');
  const cardStyle = getResultCardStyle(configurations, option.percentage);
  const highlightColor = Utils.keyValue(configurations, 'survey_results_highlight_color', '#7FAE1B');
  
  const colSize = cardSize === 'small' ? { md: "3", sm: "4", xs: "6" } : { md: "4", sm: "6" };
  const bodyPadding = cardSize === 'small' ? '0.75rem' : '1rem';
  
  // Check if this was the user's selection
  const optionId = option.id || option.option_id;
  const isUserSelection = userSelectedOptionId && optionId && optionId.toString() === String(userSelectedOptionId);
  
  // Enhanced card style for correct answers and user selections
  const enhancedCardStyle = {
    ...cardStyle,
    border: option.is_correct 
      ? `2px solid #28a745` 
      : isUserSelection 
        ? `2px solid ${highlightColor}` 
        : cardStyle.border,
    backgroundColor: option.is_correct 
      ? '#f8fff9' 
      : isUserSelection 
        ? '#fff8e1' 
        : cardStyle.backgroundColor
  };

  return (
    <ReactStrap.Col {...colSize} className="mb-4">
      <ReactStrap.Card style={enhancedCardStyle}>
        <ReactStrap.CardBody style={{ padding: bodyPadding }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <h5 style={{
                margin: 0,
                marginBottom: '0.5rem',
                fontWeight: 500,
                color: '#343a40',
                fontSize: cardSize === 'small' ? '0.9rem' : '1rem',
                flex: 1
              }}>
                {option.title}
              </h5>
              <div style={{ display: 'flex', gap: '0.25rem', marginLeft: '0.5rem' }}>
                {option.is_correct && (
                  <span title="Correct Answer" style={{
                    fontSize: '1.2rem',
                    color: '#28a745'
                  }}>
                    ✓
                  </span>
                )}
                {isUserSelection && (
                  <span title="Your Selection" style={{
                    fontSize: '1rem',
                    color: highlightColor
                  }}>
                    👆
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ marginTop: 'auto' }}>
              <div style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '0.5rem'
              }}>
                <div style={{
                  width: `${option.percentage}%`,
                  height: '100%',
                  backgroundColor: highlightColor,
                  borderRadius: '4px',
                  transition: 'width 0.5s ease'
                }}></div>
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.9rem'
              }}>
                <span style={{
                  fontWeight: 600,
                  color: highlightColor,
                  fontSize: cardSize === 'small' ? '1.2rem' : '1.4rem'
                }}>
                  {option.percentage.toFixed(1)}%
                </span>
                <span style={{ color: '#6c757d' }}>
                  {option.response_count} votes
                </span>
              </div>
            </div>
          </div>
        </ReactStrap.CardBody>
      </ReactStrap.Card>
    </ReactStrap.Col>
  );
};

const sectionStyle = {
  marginBottom: '3rem',
  padding: '2rem',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.08)',
  border: '1px solid #f0f0f0',
};

const getSectionStyle = (configurations: any, borderColor: string = '#7FAE1B') => ({
  ...sectionStyle,
  borderLeft: `4px solid ${borderColor}`,
});

const needsHeader = (surveyResults: any): boolean => {
  return Utils.isTruthy(Utils.keyValue(surveyResults.configurations, 'survey_results_header_show', 'true'));
};

const HeaderComponent = ({configurations}: any) => {
  const headerImageUrl = Utils.keyValue(configurations, 'survey_results_header_image_url', '') ||
                         Utils.keyValue(configurations, 'brand_header_image_url', '');
  const headerText = Utils.keyValue(configurations, 'survey_results_header_text', '') ||
                    Utils.keyValue(configurations, 'brand_header_text', '');

  return  <ReactStrap.Navbar className="rasa-public-header">
    <ReactStrap.Nav className="center">
      <ReactStrap.NavItem className="center">
        {headerImageUrl && <img className="brand-header" src={headerImageUrl}/>}
        {headerText && <div className="brand-header-text"
          dangerouslySetInnerHTML={{__html: headerText}}>
        </div>}
      </ReactStrap.NavItem>
    </ReactStrap.Nav>
  </ReactStrap.Navbar>
};

const LoadingCard = () => (
  <ReactStrap.Col md="4" sm="6" className="mb-4">
    <ReactStrap.Card style={{
      transition: 'all 0.3s ease',
      borderRadius: '8px',
      border: '1px solid #dee2e6',
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
      height: '100%',
    }}>
      <ReactStrap.CardBody>
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{ width: '80%', height: '1.5rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
        </div>
        <div style={{ marginTop: 'auto' }}>
          <div style={{ width: '100%', height: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '0.5rem' }}></div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ width: '30%', height: '1rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
            <div style={{ width: '40%', height: '1rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}></div>
          </div>
        </div>
      </ReactStrap.CardBody>
    </ReactStrap.Card>
  </ReactStrap.Col>
);

class SurveyResults extends React.Component<Props> {
  private hasDispatchedRedirect = false;

  componentDidMount() {
    // Reset redirect flag for fresh page load
    this.hasDispatchedRedirect = false;
    
    // Parse URL parameters and trigger data loading
    this.loadPageData();
    
    this.checkForRedirect();
    
    // Workaround: Check state after async actions complete
    setTimeout(() => {
      if (this.props.surveyResults.isAlreadySubmitted) {
        this.forceUpdate();
        // Also check for redirect after the re-render
        this.checkForRedirect();
      }
    }, 1000);
  }

  loadPageData = () => {
    const url = new URL(window.location.href);
    const pathParts = url.pathname.split('/');
    const communityIdentifier = pathParts[2]; // Assuming URL pattern like /survey_results/:community_identifier/:survey_id
    
    const qs = queryString.parse(url.search);
    const token = qs.token as string;
    const surveyOptionId = qs.survey_option_id as string;
    const surveyId = qs.survey_id as string;
    
    
    if (communityIdentifier && surveyId) {
      this.props.loadSurveyResultsPage(communityIdentifier, surveyId, token, surveyOptionId);
    }
  };

  componentDidUpdate(prevProps: Props) {
    // Only check for redirect if survey data has changed
    if (prevProps.surveyResults.survey?.id !== this.props.surveyResults.survey?.id) {
      this.checkForRedirect();
    }
    
    // Check if isAlreadySubmitted state changed - force re-render and check for redirect
    if (prevProps.surveyResults.isAlreadySubmitted !== this.props.surveyResults.isAlreadySubmitted) {
      // Force re-render to ensure the feedback message appears
      this.forceUpdate();
      // Also check for redirect when alreadySubmitted changes
      this.checkForRedirect();
    }
    
    // Check for redirect when error message changes (for already submitted detection)
    if (prevProps.surveyResults.errorMessage !== this.props.surveyResults.errorMessage) {
      this.checkForRedirect();
    }
  }

  checkForRedirect = () => {
    const { surveyResults } = this.props;
    const userSelectedOptionId = surveyResults.surveyOptionId;
    const hideResults = surveyResults.survey?.hide_results === true;
    const finalUrl = surveyResults.survey?.final_url;
    const hasToken = surveyResults.token;
    const surveyId = surveyResults.survey?.id || surveyResults.surveyId;

    // Early return if we've already dispatched redirect
    if (this.hasDispatchedRedirect) {
      return;
    }

    // Early return if we don't have the basic requirements
    if (!hideResults || !userSelectedOptionId || !finalUrl) {
      return;
    }

    // For immediate redirect case (no token) - redirect right away
    if (!hasToken && surveyId) {
      this.hasDispatchedRedirect = true;
      this.props.redirectToFinalUrl(finalUrl);
      return;
    }
    
    // For token case: redirect when not loading and we have all required data
    if (hasToken && !surveyResults.isLoading && surveyId) {
      // Check if already submitted (either flag or error message)
      const isAlreadySubmitted = surveyResults.isAlreadySubmitted || 
                                  (surveyResults.errorMessage && surveyResults.errorMessage.includes('already exists'));
      
      // For already submitted case - redirect
      if (isAlreadySubmitted) {
        this.hasDispatchedRedirect = true;
        this.props.redirectToFinalUrl(finalUrl);
        return;
      }
      
      // FALLBACK: If we have token, not loading, and no errors, redirect anyway
      // This handles the case where the store action completed successfully
      if (!surveyResults.errored) {
        this.hasDispatchedRedirect = true;
        this.props.redirectToFinalUrl(finalUrl);
        return;
      }
    }
  };


  render() {
    const { surveyResults } = this.props;

    // URL parameters are now handled by the epic and stored in state
    const surveyId = surveyResults.surveyId || '';
    const communityIdentifier = surveyResults.identifier || '';
    
    // Check hide results logic
    const userSelectedOptionId = surveyResults.surveyOptionId;
    const hideResults = surveyResults.survey?.hide_results === true;
    const finalUrl = surveyResults.survey?.final_url;
    const hasToken = surveyResults.token;

    // Only show loading for configurations - don't wait for survey data
    const configurationsLoaded = surveyResults.configurations && Array.isArray(surveyResults.configurations) && surveyResults.configurations.length > 0;
    
    
    if (!configurationsLoaded) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '1.5rem',
              color: '#6c757d',
              marginBottom: '1rem'
            }}>
              Loading...
            </div>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #7FAE1B',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      );
    }
    
    // Show loading/redirecting states - redirect for both already submitted and new submissions
    if (hideResults && userSelectedOptionId && finalUrl) {
      // If we have token and still loading, show saving message
      if (hasToken && surveyResults.isLoading) {
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '1.5rem',
                color: '#6c757d',
                marginBottom: '1rem'
              }}>
                Saving your response...
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #7FAE1B',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        );
      } else if (hasToken && !surveyResults.isLoading) {
        // Token case: result has been saved, epic should handle redirect
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '1.5rem',
                color: '#6c757d',
                marginBottom: '1rem'
              }}>
                Redirecting...
              </div>
            </div>
          </div>
        );
      } else if (!hasToken) {
        // No token - show redirecting message (redirect dispatched in lifecycle methods)
        return (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: '1.5rem',
                color: '#6c757d',
                marginBottom: '1rem'
              }}>
                Redirecting...
              </div>
            </div>
          </div>
        );
      }
    }
    
    // Always show results - don't hide them even if already submitted
    const shouldHideResults = false;
    
    // Feedback logic: ONLY show feedback when we have store API response
    let showFeedback = false;
    let feedbackType: 'success' | 'fail' | 'already_submitted' = 'success';
    let feedbackMessage = '';
    
    // Only show feedback if we have token + option + API response
    if (hasToken && userSelectedOptionId && surveyResults.hasFeedback) {
      showFeedback = true;
      feedbackMessage = surveyResults.feedbackMessage;
      
      if (surveyResults.isAlreadySubmitted) {
        feedbackType = 'already_submitted';
      } else if (surveyResults.isCorrect) {
        feedbackType = 'success';
      } else {
        feedbackType = 'fail';
      }
    }

    // Get configurable colors for feedback UI
    const successBgColor = Utils.keyValue(surveyResults.configurations, 'survey_results_success_bg_color', '#d4edda');
    const successTextColor = Utils.keyValue(surveyResults.configurations, 'survey_results_success_text_color', '#155724');
    const failBgColor = Utils.keyValue(surveyResults.configurations, 'survey_results_fail_bg_color', '#f8d7da');
    const failTextColor = Utils.keyValue(surveyResults.configurations, 'survey_results_fail_text_color', '#721c24');

    return (
      <ReactStrap.Container style={{ paddingTop: '2rem', paddingBottom: '3rem' }}>
        <ReactStrap.Row>
          <ReactStrap.Col>
            {needsHeader(surveyResults) && <HeaderComponent configurations={surveyResults.configurations} />}
            <header className="page-header text-center" style={{
              marginBottom: '1rem',
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: '#2c3e50'
            }}>
              {Utils.keyValue(surveyResults.configurations, 'survey_results_page_title', 'Survey Results')}
            </header>
            <div className="lead-text text-center" style={{
              color: '#6c757d',
              marginBottom: '3rem',
              lineHeight: 1.6,
              fontSize: '1.2rem',
              maxWidth: '600px',
              margin: '0 auto 3rem'
            }}
            dangerouslySetInnerHTML={{
              __html: Utils.keyValue(surveyResults.configurations, 'survey_results_header_description', 
                      'See how others responded to this survey. Results are updated in real-time.')
            }}>
            </div>
          </ReactStrap.Col>
        </ReactStrap.Row>

        {/* User Feedback Message */}
        {showFeedback && (
          <div style={{
            ...getSectionStyle(surveyResults.configurations, 
              feedbackType === 'success' ? '#28a745' : 
              feedbackType === 'fail' ? '#dc3545' : 
              feedbackType === 'already_submitted' ? '#6f42c1' : '#7FAE1B'),
            backgroundColor: 
              feedbackType === 'success' ? successBgColor : 
              feedbackType === 'fail' ? failBgColor : 
              feedbackType === 'already_submitted' ? '#e7e3ff' : '#e7f3ff',
            color: 
              feedbackType === 'success' ? successTextColor : 
              feedbackType === 'fail' ? failTextColor : 
              feedbackType === 'already_submitted' ? '#3d1a78' : '#004085',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: 500,
            marginBottom: '2rem'
          }}>
            {feedbackType === 'success' && (
              <>
                <h4 style={{ 
                  color: successTextColor, 
                  marginBottom: '0.5rem',
                  fontSize: '1.3rem'
                }}>
                  🎉 Correct!
                </h4>
                <p style={{ margin: 0, color: successTextColor }}>
                  {feedbackMessage}
                </p>
              </>
            )}
            {feedbackType === 'fail' && (
              <>
                <h4 style={{ 
                  color: failTextColor, 
                  marginBottom: '0.5rem',
                  fontSize: '1.3rem'
                }}>
                  ❌ Not quite right
                </h4>
                <p style={{ margin: 0, color: failTextColor }}>
                  {feedbackMessage}
                </p>
              </>
            )}
            {feedbackType === 'already_submitted' && (
              <>
                <h4 style={{ 
                  color: '#3d1a78', 
                  marginBottom: '0.5rem',
                  fontSize: '1.3rem'
                }}>
                  ✅ Already Submitted
                </h4>
                <p style={{ margin: 0, color: '#3d1a78' }}>
                  {feedbackMessage}
                </p>
              </>
            )}
          </div>
        )}

        {/* Show message when results are hidden */}
        {shouldHideResults && (
          <div style={{
            ...getSectionStyle(surveyResults.configurations, '#6c757d'),
            backgroundColor: '#f8f9fa',
            color: '#6c757d',
            textAlign: 'center',
            fontSize: '1.1rem',
            marginBottom: '2rem'
          }}>
            <h4 style={{ color: '#6c757d', marginBottom: '0.5rem' }}>
              🔒 Results Hidden
            </h4>
            <p style={{ margin: 0, color: '#6c757d' }}>
              Thank you for participating! Results are not shown for this survey.
            </p>
          </div>
        )}

        {surveyResults.errored && (
          <ReactStrap.Alert color="danger" className="mb-4">
            {surveyResults.errorMessage || 'An error occurred while loading results. Please try again.'}
          </ReactStrap.Alert>
        )}

        {/* Survey Question Section */}
        <div style={getSectionStyle(surveyResults.configurations, '#7FAE1B')}>
          <div style={{ marginBottom: '1.5rem' }}>
            <header className="section-header-grey">Survey Question</header>
            <h4 style={{
              color: '#495057',
              marginBottom: '1rem',
              fontSize: '1.3rem',
              fontWeight: 400
            }}>
              {surveyResults.survey?.question || 'Loading...'}
            </h4>
            <div style={{
              display: 'flex',
              gap: '2rem',
              alignItems: 'center',
              fontSize: '0.9rem',
              color: '#6c757d'
            }}>
              <div>
                <strong>Total Responses:</strong> {surveyResults.totalResponses || 0}
              </div>
            </div>
          </div>
        </div>

        {/* Results Section - Hide if shouldHideResults is true */}
        {!shouldHideResults && (
          <div style={getSectionStyle(surveyResults.configurations, '#17a2b8')}>
            <div style={{ marginBottom: '1.5rem' }}>
              <header className="section-header-grey">Response Breakdown</header>
              <div style={{ color: '#6c757d', marginBottom: '1.5rem' }}>
                Here's how people responded to this survey:
              </div>
            </div>
            
            <ReactStrap.Row>
              {surveyResults.isLoading ? (
                <>
                  <LoadingCard />
                  <LoadingCard />
                  <LoadingCard />
                  <LoadingCard />
                </>
              ) : surveyResults.options && surveyResults.options.length > 0 ? (
                surveyResults.options
                  .sort((a, b) => b.percentage - a.percentage) // Sort by percentage descending
                  .map((option, index) => (
                    <ResultCard 
                      key={option.id || option.option_id || `option-${index}`} 
                      option={option} 
                      configurations={surveyResults.configurations}
                      userSelectedOptionId={userSelectedOptionId}
                    />
                  ))
              ) : (
                <ReactStrap.Col>
                  <div className="text-center" style={{ 
                    padding: '3rem', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    border: '1px solid #dee2e6' 
                  }}>
                    <span style={{ fontSize: '3rem', marginBottom: '1rem', display: 'block' }}>📊</span>
                    <h5 style={{ color: '#6c757d', marginBottom: '0.5rem' }}>No Responses Yet</h5>
                    <p className="text-muted mb-0">
                      This survey hasn't received any responses yet. Check back later to see the results!
                    </p>
                  </div>
                </ReactStrap.Col>
              )}
            </ReactStrap.Row>
          </div>
        )}

        {/* Additional Stats Section - Hide if shouldHideResults is true */}
        {!shouldHideResults && surveyResults.options && surveyResults.options.length > 0 && (
          <div style={getSectionStyle(surveyResults.configurations, '#ffc107')}>
            <div style={{ marginBottom: '1.5rem' }}>
              <header className="section-header-grey">Quick Stats</header>
            </div>
            
            <ReactStrap.Row>
              <ReactStrap.Col md="3" sm="6" className="mb-3">
                <div className="text-center p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4 style={{ color: '#7FAE1B', marginBottom: '0.5rem' }}>
                    {surveyResults.options.reduce((max, option) => Math.max(max, option.percentage), 0).toFixed(1)}%
                  </h4>
                  <small className="text-muted">Top Choice</small>
                </div>
              </ReactStrap.Col>
              <ReactStrap.Col md="3" sm="6" className="mb-3">
                <div className="text-center p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4 style={{ color: '#17a2b8', marginBottom: '0.5rem' }}>
                    {surveyResults.options.length}
                  </h4>
                  <small className="text-muted">Total Options</small>
                </div>
              </ReactStrap.Col>
              <ReactStrap.Col md="3" sm="6" className="mb-3">
                <div className="text-center p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4 style={{ color: '#ffc107', marginBottom: '0.5rem' }}>
                    {(surveyResults.options.reduce((sum, option) => sum + option.percentage, 0) / surveyResults.options.length).toFixed(1)}%
                  </h4>
                  <small className="text-muted">Average</small>
                </div>
              </ReactStrap.Col>
              <ReactStrap.Col md="3" sm="6" className="mb-3">
                <div className="text-center p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h4 style={{ color: '#dc3545', marginBottom: '0.5rem' }}>
                    {surveyResults.options.filter(option => option.percentage > 0).length}
                  </h4>
                  <small className="text-muted">Options with Votes</small>
                </div>
              </ReactStrap.Col>
            </ReactStrap.Row>
          </div>
        )}
      </ReactStrap.Container>
    );
  }
}

const mapStateToProps = ({ surveyResults }: WrappedSurveyResults) => ({ surveyResults });

const mapDispatchToProps = {
  fetchSurveyResults: Actions.fetchSurveyResults,
  redirectToFinalUrl: Actions.redirectToFinalUrl,
  loadSurveyResultsPage: Actions.loadSurveyResultsPage,
};

export const SurveyResultsConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(SurveyResults);